// ── user-deactivate-modal: the deactivate confirm Modal (POST /users/:id/...)
// Wave 1 (Users). Deactivation is preferred over deletion: POST
// /users/:id/deactivate tombstones the user (excluded from the default
// projection). This modal is a deliberate confirmation step; the page owns the
// actual POST and toasts only after the backend confirms. A refused write
// (permission-denied / gate-closed / unavailable) reads as its honest message
// inline and the dialog stays open — a closed write NEVER implies the user was
// deactivated.

import { Alert, Button, Modal } from "@vjal/ui";

import { destructiveButtonClass } from "../../components/ui-classes";
import type { CoachingUserDto } from "../../services/coaching-dtos";
import { describeUserWriteError } from "./users-write";

export interface UserDeactivateModalProps {
  open: boolean;
  user: CoachingUserDto | null;
  onClose: () => void;
  onConfirm: () => Promise<unknown>;
  submitting?: boolean;
  submitError?: unknown;
}

export function UserDeactivateModal({
  open,
  user,
  onClose,
  onConfirm,
  submitting = false,
  submitError
}: UserDeactivateModalProps) {
  const topError = describeUserWriteError(submitError);
  const name = user?.name || user?.email || "this user";

  return (
    <Modal open={open} onClose={onClose} title="Deactivate user">
      <div className="flex flex-col gap-4">
        {topError && <Alert variant={topError.tone === "warning" ? "warning" : "error"}>{topError.message}</Alert>}

        <Alert variant="warning">
          Deactivating <span className="font-semibold">{name}</span> revokes access and removes them from the active
          roster. Deactivation is preferred over deletion — their history is preserved and they can be reactivated later
          from the edit form.
        </Alert>

        <div className="mt-2 flex items-center justify-end gap-3">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="danger"
            className={destructiveButtonClass}
            isLoading={submitting}
            onClick={() => {
              void onConfirm().catch(() => {
                // The owning mutation surfaces the error via `submitError`.
              });
            }}
          >
            Deactivate user
          </Button>
        </div>
      </div>
    </Modal>
  );
}
